<template>
  <section class="avisos" :aria-label="$t('clan.avisos.titulo')">
    <p class="avisos__rotulo">
      {{ $t('clan.avisos.titulo') }}
      <button
        v-if="mural.souOLider"
        type="button"
        class="avisos__editar"
        @click="abrirEditorDoTexto"
      >
        {{ mural.texto ? $t('clan.avisos.editarRegras') : $t('clan.avisos.escreverRegras') }}
      </button>
    </p>

    <!--
      A ZONA DE CIMA — regras e boas-vindas. É a primeira coisa que um membro
      novo lê, e por isso vem antes: um aviso de evento acima das regras faria a
      pessoa entrar no clã sem nunca ver o que se espera dela.
    -->
    <div v-if="mural.texto" class="avisos__regras">{{ mural.texto }}</div>
    <p v-else-if="mural.souOLider" class="avisos__vazio">
      {{ $t('clan.avisos.semRegrasLider') }}
    </p>

    <!-- A ZONA DE BAIXO — os avisos de evento, fixados primeiro. -->
    <ul v-if="mural.avisos.length" class="avisos__lista">
      <li
        v-for="aviso in mural.avisos"
        :key="aviso.id"
        :class="{ 'avisos__item--fixado': aviso.fixado }"
      >
        <div class="avisos__cabecalho">
          <FishIcon v-if="aviso.fixado" nome="star" tamanho="miudo" />
          <strong class="crescer">{{ aviso.titulo }}</strong>
          <span class="avisos__data t-numero">{{ emData(aviso.quando) }}</span>
        </div>
        <p class="avisos__corpo">{{ aviso.corpo }}</p>
        <p class="avisos__assinatura">
          <!-- Autor nulo é quem saiu do jogo: o aviso fica, sem assinatura. -->
          {{ aviso.autor || $t('clan.avisos.autorDesconhecido') }}
          <span v-if="mural.souOLider" class="avisos__mando">
            <button type="button" class="avisos__mandinho" @click="fixar(aviso)">
              {{ aviso.fixado ? $t('clan.avisos.soltar') : $t('clan.avisos.fixar') }}
            </button>
            <button type="button" class="avisos__mandinho" @click="remover(aviso)">
              {{ $t('clan.avisos.apagar') }}
            </button>
          </span>
        </p>
      </li>
    </ul>

    <EmptyState
      v-else-if="!mural.texto"
      icone="inbox"
      :titulo="$t('clan.avisos.vazio')"
      :explicacao="mural.souOLider ? $t('clan.avisos.vazioLider') : $t('clan.avisos.vazioMembro')"
    />

    <FishButton
      v-if="mural.souOLider"
      tamanho="pequeno"
      icone="plus"
      som="clique"
      @click="abrirEditorDeAviso()"
    >
      {{ $t('clan.avisos.novo') }}
    </FishButton>

    <!-- O editor: um só para as duas zonas, com o campo de título escondido no
         modo "regras" — são a mesma ação de escrever, e dois modais seriam duas
         telas para a mesma coisa. -->
    <FishModal :aberto="editor.aberto" :titulo="tituloDoEditor" @fechar="fecharEditor">
      <div class="avisos__editor">
        <label v-if="editor.modo === 'aviso'" class="avisos__campo">
          <span>{{ $t('clan.avisos.tituloDoAviso') }}</span>
          <input v-model="editor.titulo" type="text" maxlength="120" />
        </label>

        <label class="avisos__campo">
          <span>{{ editor.modo === 'aviso' ? $t('clan.avisos.corpo') : $t('clan.avisos.regras') }}</span>
          <textarea
            v-model="editor.corpo"
            :rows="editor.modo === 'aviso' ? 5 : 8"
            :maxlength="editor.modo === 'aviso' ? 2000 : 4000"
          />
        </label>

        <label v-if="editor.modo === 'aviso'" class="avisos__fixar">
          <input v-model="editor.fixado" type="checkbox" />
          {{ $t('clan.avisos.fixarNoTopo') }}
        </label>

        <p v-if="erro" class="avisos__erro">{{ erro }}</p>

        <FishButton
          variante="primario"
          bloco
          :desabilitado="!podeSalvar"
          :carregando="salvando"
          som="clique"
          @click="salvar"
        >
          {{ $t('clan.avisos.salvar') }}
        </FishButton>
      </div>
    </FishModal>
  </section>
</template>

<script>
import { FishButton, FishIcon, FishModal, EmptyState } from '@/shared/ui';
import * as v2 from '@/core/services/api/v2';

/**
 * O MURAL DE AVISOS DO CLÃ — o quadro do Ikariam (pedido do autor, 04/09).
 *
 * Duas zonas com vidas diferentes, e a ordem entre elas não é arbitrária: as
 * **regras e boas-vindas** ficam em cima porque são a primeira coisa que um
 * membro novo lê; os **avisos de evento** ficam embaixo, fixados primeiro e
 * recentes antes.
 *
 * **Não é o quadro** (`ClanQuadro`). Aquele é escrito pelo sistema e diz o que o
 * clã fez; este é escrito pelo líder e diz o que ele quer que se saiba. Os dois
 * existem e não se substituem.
 *
 * O `souOLider` que vem do servidor serve para **desenhar o botão** — quem
 * autoriza é o servidor, a cada escrita. Esconder o botão é evitar mostrar uma
 * porta que não abre (D11), e não uma trava.
 */
export default {
  name: 'ClanAvisos',
  components: { FishButton, FishIcon, FishModal, EmptyState },
  emits: ['erro'],
  data() {
    return {
      mural: { souOLider: false, texto: null, textoAtualizadoEm: null, avisos: [] },
      editor: { aberto: false, modo: 'aviso', id: null, titulo: '', corpo: '', fixado: false },
      salvando: false,
      erro: '',
    };
  },
  computed: {
    tituloDoEditor() {
      if (this.editor.modo === 'regras') return this.$t('clan.avisos.regras');
      return this.editor.id ? this.$t('clan.avisos.editarAviso') : this.$t('clan.avisos.novo');
    },
    podeSalvar() {
      if (this.salvando) return false;
      if (this.editor.modo === 'regras') return true; // Vazio apaga, e isso é válido.
      return Boolean(this.editor.titulo.trim() && this.editor.corpo.trim());
    },
  },
  created() {
    void this.carregar();
  },
  methods: {
    async carregar() {
      try {
        this.mural = await v2.muralDoCla(this.$store.state.player.id);
      } catch (error) {
        // Sem clã ainda é 404, e não é erro: este componente só é montado dentro
        // do clã, mas a corrida entre sair e recarregar existe.
        if (error?.response?.status !== 404) this.$emit('erro', error);
      }
    },

    emData(iso) {
      return new Date(iso).toLocaleDateString(this.$i18n.locale);
    },

    abrirEditorDoTexto() {
      this.erro = '';
      this.editor = {
        aberto: true,
        modo: 'regras',
        id: null,
        titulo: '',
        corpo: this.mural.texto || '',
        fixado: false,
      };
    },

    abrirEditorDeAviso(aviso) {
      this.erro = '';
      this.editor = {
        aberto: true,
        modo: 'aviso',
        id: aviso?.id ?? null,
        titulo: aviso?.titulo ?? '',
        corpo: aviso?.corpo ?? '',
        fixado: aviso?.fixado ?? false,
      };
    },

    fecharEditor() {
      this.editor.aberto = false;
      this.erro = '';
    },

    async salvar() {
      this.salvando = true;
      this.erro = '';
      const playerId = this.$store.state.player.id;

      try {
        if (this.editor.modo === 'regras') {
          // Texto em branco APAGA — `null` e `''` são coisas diferentes para
          // quem lê, e um bloco de regras vazio na tela é pior que nenhum.
          await v2.salvarTextoDoMural(playerId, this.editor.corpo.trim() || null);
        } else if (this.editor.id) {
          await v2.editarAviso(playerId, this.editor.id, {
            titulo: this.editor.titulo,
            corpo: this.editor.corpo,
            fixado: this.editor.fixado,
          });
        } else {
          await v2.publicarAviso(playerId, {
            titulo: this.editor.titulo,
            corpo: this.editor.corpo,
            fixado: this.editor.fixado,
          });
        }
        this.fecharEditor();
        await this.carregar();
      } catch (error) {
        // A mensagem do servidor vai inteira para a tela: é ela que diz "já há
        // três avisos fixados", e trocá-la por "algo deu errado" apagaria a
        // única informação útil.
        this.erro = error?.response?.data?.message || this.$t('clan.avisos.falhou');
      } finally {
        this.salvando = false;
      }
    },

    async fixar(aviso) {
      try {
        await v2.editarAviso(this.$store.state.player.id, aviso.id, { fixado: !aviso.fixado });
        await this.carregar();
      } catch (error) {
        this.$emit('erro', error?.response?.data?.message || error);
      }
    },

    async remover(aviso) {
      try {
        await v2.removerAviso(this.$store.state.player.id, aviso.id);
        await this.carregar();
      } catch (error) {
        this.$emit('erro', error);
      }
    },
  },
};
</script>

<style scoped>
.avisos {
  display: flex;
  flex-direction: column;
  gap: var(--e-2);
}

.avisos__rotulo {
  display: flex;
  align-items: baseline;
  gap: var(--e-2);
  margin: 0;
  font-family: var(--fonte-display);
  font-size: var(--f-2);
  color: var(--tinta-fraca);
  border-bottom: 2px dashed var(--papel-sombra);
  padding-bottom: var(--e-1);
}

.avisos__editar {
  margin-left: auto;
  background: none;
  border: 0;
  padding: 0;
  font: inherit;
  font-size: var(--f-1);
  color: var(--latao);
  text-decoration: underline dashed;
  cursor: pointer;
}

/* As regras são texto corrido do líder: `pre-wrap` porque ele quebra linha de
 * propósito, e colapsar isso transformaria uma lista de regras num parágrafo. */
.avisos__regras {
  white-space: pre-wrap;
  font-family: var(--fonte-corpo);
  font-size: var(--f-2);
  line-height: 1.5;
  color: var(--tinta);
  border-left: 3px solid var(--latao);
  padding-left: var(--e-2);
}

.avisos__vazio {
  margin: 0;
  font-size: var(--f-1);
  color: var(--tinta-fraca);
  font-style: italic;
}

.avisos__lista {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: var(--e-2);
}

.avisos__lista li {
  border-bottom: 1px dashed var(--papel-sombra);
  padding-bottom: var(--e-2);
}

/* O fixado ganha a marca do lápis, e não um fundo: papel não tem realce. */
.avisos__item--fixado .avisos__cabecalho strong {
  color: var(--latao);
}

.avisos__cabecalho {
  display: flex;
  align-items: baseline;
  gap: var(--e-1);
}

.avisos__data {
  font-size: var(--f-1);
  color: var(--tinta-fraca);
}

.avisos__corpo {
  margin: var(--e-1) 0 0;
  white-space: pre-wrap;
  font-family: var(--fonte-corpo);
  font-size: var(--f-2);
  line-height: 1.4;
  color: var(--tinta);
}

.avisos__assinatura {
  display: flex;
  align-items: center;
  gap: var(--e-2);
  margin: var(--e-1) 0 0;
  font-size: var(--f-1);
  color: var(--tinta-fraca);
}

.avisos__mando {
  margin-left: auto;
  display: flex;
  gap: var(--e-2);
}

.avisos__mandinho {
  background: none;
  border: 0;
  padding: 0;
  font: inherit;
  font-size: var(--f-1);
  color: var(--latao);
  cursor: pointer;
}

.avisos__editor {
  display: flex;
  flex-direction: column;
  gap: var(--e-2);
}

.avisos__campo {
  display: flex;
  flex-direction: column;
  gap: var(--e-1);
}

.avisos__campo > span {
  font-size: var(--f-1);
  color: var(--tinta-fraca);
}

.avisos__fixar {
  display: flex;
  align-items: center;
  gap: var(--e-1);
  font-size: var(--f-2);
  color: var(--tinta);
}

.avisos__erro {
  margin: 0;
  font-size: var(--f-1);
  color: var(--ruim, #a33);
}
</style>
